import React from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { GridColDef } from '@mui/x-data-grid';
import { Button, Box, Typography, Alert, Snackbar, TextField } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';

import { DataTable } from '../components/DataTable';
import { FormDialog } from '../components/FormDialog';
import { productEquivalencyCategoriesApi } from '../api/services';
import { extractApiError } from '../utils/apiErrors';
import type { ProductEquivalencyCategory } from '../api/config';

export const ProductEquivalencyCategoriesPage: React.FC = () => {
  const [isFormOpen, setIsFormOpen] = React.useState(false);
  const [selectedCategory, setSelectedCategory] = React.useState<ProductEquivalencyCategory | undefined>();
  const [formName, setFormName] = React.useState('');
  const [snackbar, setSnackbar] = React.useState<{ message: string; severity: 'error' | 'success' } | null>(null);

  const queryClient = useQueryClient();

  const { data: categories = [], isLoading, error } = useQuery({
    queryKey: ['product_equivalency_categories'],
    queryFn: productEquivalencyCategoriesApi.getAll,
  });

  const createMutation = useMutation({
    mutationFn: productEquivalencyCategoriesApi.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['product_equivalency_categories'] });
      setSnackbar({ message: 'Category created successfully', severity: 'success' });
      handleCloseForm();
    },
    onError: (error) => {
      setSnackbar({ message: extractApiError(error, 'Failed to create category'), severity: 'error' });
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<{ name: string }> }) =>
      productEquivalencyCategoriesApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['product_equivalency_categories'] });
      setSnackbar({ message: 'Category updated successfully', severity: 'success' });
      handleCloseForm();
    },
    onError: (error) => {
      setSnackbar({ message: extractApiError(error, 'Failed to update category'), severity: 'error' });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: productEquivalencyCategoriesApi.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['product_equivalency_categories'] });
      setSnackbar({ message: 'Category deleted successfully', severity: 'success' });
    },
    onError: (error) => {
      setSnackbar({ message: extractApiError(error, 'Failed to delete category'), severity: 'error' });
    },
  });

  const mutationError = createMutation.error || updateMutation.error;

  const columns: GridColDef[] = [
    { field: 'id', headerName: 'ID', width: 80 },
    { field: 'name', headerName: 'Name', flex: 1, minWidth: 300 },
  ];

  const handleCloseForm = () => {
    setIsFormOpen(false);
    setSelectedCategory(undefined);
    setFormName('');
    createMutation.reset();
    updateMutation.reset();
  };

  const handleOpenCreate = () => {
    setSelectedCategory(undefined);
    setFormName('');
    setIsFormOpen(true);
  };

  const handleEdit = (id: string) => {
    const category = categories.find((c) => c.id.toString() === id);
    if (category) {
      setSelectedCategory(category);
      setFormName(category.name);
      setIsFormOpen(true);
    }
  };

  const handleDelete = (id: string) => {
    if (window.confirm('Are you sure you want to delete this category?')) {
      deleteMutation.mutate(Number(id));
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formName.trim()) return;
    createMutation.reset();
    updateMutation.reset();
    if (selectedCategory) {
      updateMutation.mutate({ id: selectedCategory.id, data: { name: formName.trim() } });
    } else {
      createMutation.mutate({ name: formName.trim() });
    }
  };

  if (error) {
    return <Alert severity="error">Error loading equivalency categories</Alert>;
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h5" component="h1">
          Equivalency Categories
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleOpenCreate}
        >
          Add Category
        </Button>
      </Box>

      <DataTable
        rows={categories}
        columns={columns}
        loading={isLoading}
        onEditClick={handleEdit}
        onDeleteClick={handleDelete}
        getRowId={(row: ProductEquivalencyCategory) => row.id.toString()}
        fullHeight
      />

      <FormDialog
        open={isFormOpen}
        onClose={handleCloseForm}
        onSubmit={handleSubmit}
        title={selectedCategory ? 'Edit Category' : 'Create Category'}
        submitLabel={selectedCategory ? 'Update' : 'Create'}
        error={mutationError ? extractApiError(mutationError) : undefined}
        isPending={createMutation.isPending || updateMutation.isPending}
        submitDisabled={!formName.trim()}
      >
        <TextField
          autoFocus
          label="Name"
          value={formName}
          onChange={(e) => setFormName(e.target.value)}
          fullWidth
          required
          sx={{ mt: 1 }}
        />
      </FormDialog>

      <Snackbar
        open={!!snackbar}
        autoHideDuration={6000}
        onClose={() => setSnackbar(null)}
      >
        <Box>
          {snackbar && (
            <Alert
              severity={snackbar.severity}
              onClose={() => setSnackbar(null)}
              sx={{ width: '100%' }}
            >
              {snackbar.message}
            </Alert>
          )}
        </Box>
      </Snackbar>
    </Box>
  );
};
